import React, { useEffect } from "react";
import {
  Grid,
  TextField,
  Switch,
  FormControlLabel,
  Typography,
  Box,
  MenuItem,
  useTheme,
} from "@mui/material";

interface Props {
  config: any;
  onChange: (field: string, value: any) => void;
}

const BgrExteriorForm: React.FC<Props> = ({ config, onChange }) => {
  const theme = useTheme();
  const red = theme.palette.status.accent;

  const processor = config.processor || "";

  // --- Auto-update apiEndpoint based on profile when VUMO is selected
  useEffect(() => {
    if (processor !== "vumo") return;
    const profile = config.profile || "";
    if (!profile) return;

    let apiEndpoint = `https://api.vumography.vumo.ai/api/v1/process/single/${profile}`;
    if (profile === "platform_30_off_roll_compensation_car_crop_09") {
      apiEndpoint =
        "https://api.vumography.vumo.ai/api/v1/process/single/platform_30_off_roll_compensation_car_crop_09, https://api.vumography.vumo.ai/api/v1/process/single/platform_30_static_image";
    }
    onChange("apiEndpoint", apiEndpoint);
  }, [config.profile, processor]);

  // --- Auto-set Bright River callback when processor changes
  useEffect(() => {
    if (processor === "br") {
      onChange("callBackURL", "www.bwiportal.com/br/completed");
    }
  }, [processor]);

  return (
    <Box
      sx={{
        backgroundColor: theme.palette.background.paper,
        borderLeft: `3px solid ${red}`,
        borderRadius: 2,
        p: 2,
        mt: 1,
        boxShadow: "0 0 6px rgba(0,0,0,0.3)",
      }}
    >
      <Typography
        variant="subtitle1"
        sx={{ mb: 2, fontWeight: 600, color: red, letterSpacing: 0.3 }}
      >
        Background Replace Exterior Images
      </Typography>

      <Grid container spacing={2}>
        {/* Provider dropdown */}
        <Grid item xs={12} sm={6}>
          <TextField
            select
            label="Processor / Provider"
            fullWidth
            value={processor}
            onChange={(e) => onChange("processor", e.target.value)}
            variant="outlined"
            sx={{
              "& .MuiOutlinedInput-root": {
                "& fieldset": { borderColor: "#444" },
                "&:hover fieldset": { borderColor: red },
                "&.Mui-focused fieldset": { borderColor: red },
              },
            }}
          >
            <MenuItem value="vumo">VUMO</MenuItem>
            <MenuItem value="br">Bright River</MenuItem>
          </TextField>
        </Grid>

        {/* --- VUMO Fields --- */}
        {processor === "vumo" && (
          <>
            <Grid item xs={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={config.processRoof ?? false}
                    onChange={(e) =>
                      onChange("processRoof", e.target.checked)
                    }
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": { color: red },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                        backgroundColor: red,
                      },
                    }}
                  />
                }
                label={
                  <Typography sx={{ fontWeight: 500 }}>Process Roof</Typography>
                }
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                label="Profile"
                fullWidth
                value={config.profile || ""}
                onChange={(e) => onChange("profile", e.target.value)}
                variant="outlined"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": { borderColor: "#444" },
                    "&:hover fieldset": { borderColor: red },
                    "&.Mui-focused fieldset": { borderColor: red },
                  },
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="API Endpoint (autogenerated)"
                fullWidth
                disabled
                value={config.apiEndpoint || ""}
                variant="outlined"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": { borderColor: "#444" },
                  },
                  "& .MuiInputBase-input": { color: "#999" },
                }}
              />
            </Grid>
          </>
        )}

        {/* --- Bright River Fields --- */}
        {processor === "br" && (
          <>
            <Grid item xs={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={config.processRoof ?? false}
                    onChange={(e) =>
                      onChange("processRoof", e.target.checked)
                    }
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": { color: red },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                        backgroundColor: red,
                      },
                    }}
                  />
                }
                label={
                  <Typography sx={{ fontWeight: 500 }}>Process Roof</Typography>
                }
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                label="Specification Key (9 chars)"
                inputProps={{ maxLength: 9 }}
                fullWidth
                value={config.specificationKey || ""}
                onChange={(e) => onChange("specificationKey", e.target.value)}
                variant="outlined"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": { borderColor: "#444" },
                    "&:hover fieldset": { borderColor: red },
                    "&.Mui-focused fieldset": { borderColor: red },
                  },
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Callback URL (autogenerated)"
                fullWidth
                disabled
                value={config.callBackURL || ""}
                variant="outlined"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": { borderColor: "#444" },
                  },
                  "& .MuiInputBase-input": { color: "#999" },
                }}
              />
            </Grid>
          </>
        )}
      </Grid>
    </Box>
  );
};

export default BgrExteriorForm;
